import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { TAttributionModel } from '@feathr/blackbox';

import {
  filtersEqual,
  type IFiltersState,
  type TCampaignTypeFilter,
} from './ConversionsFiltersPopover.utils';

export interface IUseConversionsFiltersPopoverStateReturn {
  workingState: IFiltersState;
  isDirty: boolean;
  setAttr: (attr: TAttributionModel) => void;
  setProjects: (ids: string[]) => void;
  setTypes: (types: TCampaignTypeFilter[]) => void;
  reset: () => void;
}

export function useConversionsFiltersPopoverState(
  appliedFilters: IFiltersState,
): IUseConversionsFiltersPopoverStateReturn {
  const [workingState, setWorkingState] = useState<IFiltersState>(appliedFilters);
  const prevAppliedRef = useRef(appliedFilters);

  useEffect(() => {
    if (!filtersEqual(prevAppliedRef.current, appliedFilters)) {
      prevAppliedRef.current = appliedFilters;
      setWorkingState(appliedFilters);
    }
  }, [appliedFilters]);

  const isDirty = useMemo(
    () => !filtersEqual(workingState, appliedFilters),
    [workingState, appliedFilters],
  );

  const setAttr = useCallback((attr: TAttributionModel): void => {
    setWorkingState((prev) => ({ ...prev, attr }));
  }, []);

  const setProjects = useCallback((projects: string[]): void => {
    setWorkingState((prev) => ({ ...prev, projects }));
  }, []);

  const setTypes = useCallback((types: TCampaignTypeFilter[]): void => {
    setWorkingState((prev) => ({ ...prev, types }));
  }, []);

  const reset = useCallback((): void => {
    setWorkingState(appliedFilters);
  }, [appliedFilters]);

  return { workingState, isDirty, setAttr, setProjects, setTypes, reset };
}
